/**
 * Кто вошёл: аватарка и имя из Telegram.
 *
 * Зачем это на экране вообще. Вход через Telegram проходит в другом окне, и после
 * него страница выглядит точно так же, как до, — человек не знает, сработало или
 * нет, и жмёт «войти» второй раз. Второе нажатие с того же браузера безобидно, а
 * вот с телефона оно заводит второй экран и упирается в подтверждение, то есть
 * наша немота сама создаёт то, что мы потом считаем подозрительным.
 *
 * В углу стоит ИМЯ ЧЕЛОВЕКА, а не название подписки (12.08, владелец). Вопрос
 * настоящий — «под каким аккаунтом я тут», особенно когда Telegram на телефоне
 * один, а браузеров два. Подписка живёт в панели по нажатию: коротко и буквально,
 * без ролевых кличек вроде «рубильник» (13.08, владелец).
 *
 * Аватарка проксируется нашим сервером (`/v1/me/avatar`): в прямой ссылке на файл
 * Telegram лежит токен бота. Нет аватарки — рисуем инициал, и это не заглушка на
 * будущее, а нормальный конечный вид: у многих в Telegram фотографии нет.
 */

import { useEffect, useRef, useState } from 'react';

import { apiUrl } from '../lib/build';
import { adminHref, curatorHref, learnHref } from '../next/route';
import { isOwner } from './owners';
import { useSession } from './session';

/** Первая буква имени для кружка без фотографии. */
function initial(name: string | null, username: string | null): string {
  const source = (name ?? username ?? '?').trim();
  return (source[0] ?? '?').toUpperCase();
}

export function Who(): React.ReactElement | null {
  const known = useSession((s) => s.known);
  const me = useSession((s) => s.me);
  const [face, setFace] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const hide = (e: MouseEvent): void => {
      if (e.target instanceof Node && box.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', hide);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', hide);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!me.authenticated) {
      setFace(null);
      return;
    }
    let dead = false;
    let url: string | null = null;
    // Картинка тянется объектом, а не ставится в `src` адресом: ручка отдаёт 404
    // тем, у кого фотографии нет, и браузер рисовал бы сломанную картинку —
    // худший из возможных видов «всё в порядке».
    void (async () => {
      try {
        const res = await fetch(apiUrl('/v1/me/avatar'), { credentials: 'same-origin' });
        if (!res.ok || dead) return;
        url = URL.createObjectURL(await res.blob());
        setFace(url);
      } catch {
        // Нет аватарки — нет и разговора: инициал ниже нарисуется сам.
      }
    })();
    return () => {
      dead = true;
      if (url !== null) URL.revokeObjectURL(url);
    };
  }, [me.authenticated]);

  if (!known || !me.authenticated) return null;

  const plan = me.plan;
  const limited = me.reason !== null;
  // Имя из Telegram; у кого его нет — @логин. «Вход выполнен» остаётся только
  // для того, у кого нет ни имени, ни логина: пустое место рядом с аватаркой
  // читалось бы как незагрузившаяся страница.
  const who = me.name ?? (me.username === null ? 'вход выполнен' : `@${me.username}`);

  return (
    <div ref={box} className={`nx-who${limited ? ' nx-who--warn' : ''}`}>
      {/* Аватарка и имя — одна кнопка, а не кружок-кнопка рядом с текстом.
          Нажать «на себя» человек пробует по имени ровно так же, как по
          фотографии, и половина этих нажатий раньше не открывала ничего. */}
      <button
        type="button"
        className="nx-who__id"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        title={me.username === null ? 'кто вошёл' : `@${me.username}`}
      >
        <span className="nx-who__face">
          {face === null ? <span>{initial(me.name, me.username)}</span> : <img src={face} alt="" />}
        </span>
        {/* Одна строка, а не две. Вторая строка («подписка активна») делала блок
            выше всей шапки, и аватарка вставала по базовой линии текста вместо
            середины — то самое «съехала». Оговорка про демо или карантин строку
            получает: это не похвальба подпиской, а причина, по которой лента
            неполная, и молчать о ней нельзя. */}
        <span className="nx-who__text">
          <b>{who}</b>
          {limited && plan !== null && plan.note !== '' && (
            <span className="nx-lbl">{plan.note}</span>
          )}
        </span>
      </button>
      {open && (
        <div className="nx-who__panel">
          <p className="nx-who__me">
            <b>{who}</b>
            {me.name !== null && me.username !== null && <i>@{me.username}</i>}
          </p>
          {plan !== null && (
            <div className="nx-who__plan">
              <div className="nx-lbl">подписка</div>
              <b>{plan.name}</b>
              {plan.note !== '' && <span>{plan.note}</span>}
            </div>
          )}
          <p className="nx-who__seat">один экран. второй спросит в Telegram.</p>
          <div className="nx-who__actions">
            {isOwner(me) && (
              <a className="nx-who__desk" href={learnHref()}>
                Обучение
              </a>
            )}
            {isOwner(me) && (
              <a className="nx-who__desk" href={adminHref()}>
                Админка
              </a>
            )}
            {me.curator && (
              <a className="nx-who__desk" href={curatorHref()}>
                Кураторская
              </a>
            )}
            <button type="button" onClick={() => void useSession.getState().logout()}>
              выйти
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
